import { useState } from 'react';
import { SystemScanProgress } from './components/SystemScanProgress';
import { SystemScanResults } from './components/SystemScanResults';
import { PersonaSelector } from './components/PersonaSelector';
import { ModuleManager } from './components/ModuleManager';
import { Dashboard } from './components/Dashboard';
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { SystemScanResult, PersonaConfig } from './types';
import { Zap, Monitor, Users, Package, Activity } from 'lucide-react';

type AppStep = 'welcome' | 'scanning' | 'scan-results' | 'persona-selection' | 'module-management' | 'dashboard';

function App() {
  const [currentStep, setCurrentStep] = useState<AppStep>('welcome');
  const [systemScanResult, setSystemScanResult] = useState<SystemScanResult | null>(null);
  const [selectedPersona, setSelectedPersona] = useState<PersonaConfig | null>(null);
  const [startScan, setStartScan] = useState(false);

  const handleStartScan = () => {
    setStartScan(true);
    setCurrentStep('scanning');
  };

  const handleScanComplete = (result: SystemScanResult) => {
    setSystemScanResult(result);
    setCurrentStep('scan-results');
  };

  const handleContinueFromResults = () => {
    setCurrentStep('persona-selection');
  };

  const handlePersonaSelected = (persona: PersonaConfig) => {
    setSelectedPersona(persona);
    setCurrentStep('module-management');
  };

  const handleSetupComplete = () => {
    setCurrentStep('dashboard');
  };

  const handleReconfigure = () => {
    setCurrentStep('welcome');
  };

  const renderStep = () => {
    switch (currentStep) {
      case 'welcome':
        return (
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <div className="space-y-4">
              <div className="flex justify-center">
                <div className="p-4 rounded-full bg-primary/10">
                  <Zap className="w-12 h-12 text-primary" />
                </div>
              </div>
              <h1 className="text-4xl font-bold">Welcome to ChittyOS Manager</h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                The modern GUI for managing and configuring the ChittyOS ecosystem. 
                Let's set up your system based on your Mac configuration and workflow needs.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 my-12">
              <Card>
                <CardHeader className="text-center">
                  <Monitor className="w-8 h-8 mx-auto mb-2 text-blue-500" />
                  <CardTitle className="text-lg">System Scan</CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <CardDescription>
                    Automatically detect your installed applications and development tools
                  </CardDescription>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="text-center">
                  <Users className="w-8 h-8 mx-auto mb-2 text-green-500" />
                  <CardTitle className="text-lg">Persona Setup</CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <CardDescription>
                    Configure ChittyOS based on your role and workflow preferences
                  </CardDescription>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="text-center">
                  <Package className="w-8 h-8 mx-auto mb-2 text-purple-500" />
                  <CardTitle className="text-lg">Module Management</CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <CardDescription>
                    Install and configure ChittyOS modules for your specific needs
                  </CardDescription>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="text-center">
                  <Activity className="w-8 h-8 mx-auto mb-2 text-orange-500" />
                  <CardTitle className="text-lg">Monitoring</CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <CardDescription>
                    Real-time dashboard to monitor your ChittyOS ecosystem health
                  </CardDescription>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-4">
              <Button onClick={handleStartScan} size="lg" className="gap-2">
                <Monitor className="w-5 h-5" />
                Start System Scan
              </Button>
              <p className="text-sm text-muted-foreground">
                This will scan your Mac to detect applications, development tools, and potential integrations.
              </p>
            </div>
          </div>
        );

      case 'scanning':
        return (
          <SystemScanProgress
            onScanComplete={handleScanComplete}
            startScan={startScan}
          />
        );

      case 'scan-results':
        return systemScanResult ? (
          <SystemScanResults
            scanResult={systemScanResult}
            onContinue={handleContinueFromResults}
          />
        ) : null;

      case 'persona-selection':
        return (
          <PersonaSelector
            onPersonaSelected={handlePersonaSelected}
          />
        );

      case 'module-management':
        return (
          <ModuleManager
            selectedPersona={selectedPersona || undefined}
            onSetupComplete={handleSetupComplete}
          />
        );

      case 'dashboard':
        return (
          <Dashboard
            systemScanResult={systemScanResult || undefined}
            selectedPersona={selectedPersona || undefined}
            onReconfigure={handleReconfigure}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* macOS title bar spacer */}
      <div className="title-bar" />
      
      {/* Main content */}
      <div className="content-area p-6">
        {renderStep()}
      </div>
    </div>
  );
}

export default App;